// frontend/app/components/admin/LessonLibraryList.js
//
// One page of the library. Each row answers the two questions an admin opens
// this list with: which lesson is this, and is it still the file's lesson.
//
// The lock line is spelled out rather than left as a pill an admin has to know
// the meaning of. A locked lesson is one the seeder now refuses to touch, and
// that is not something anyone should have to infer from a padlock.

import React from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import LessonDownloadButton from "../lessonPlans/LessonDownloadButton";
import { formatCountLabel, PAGE_SIZE } from "./adminTable";

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

/** "IGCSE · Photosynthesis · Lesson 2 · 7 steps · updated 12/08/2026" */
const describeLesson = (item) => {
  const parts = [];
  if (item.base_board) parts.push(String(item.base_board).toUpperCase());
  if (item.topic_name) parts.push(item.topic_name);
  if (item.subject_name) parts.push(item.subject_name);
  if (item.lesson_no) parts.push(`Lesson ${item.lesson_no}`);
  parts.push(`${item.step_count || 0} step${item.step_count === 1 ? "" : "s"}`);
  const updated = formatDate(item.updated_at);
  if (updated) parts.push(`updated ${updated}`);
  return parts.join(" · ");
};

const StatusPill = ({ colors, status }) => {
  const accent =
    status === "published"
      ? colors.success
      : status === "archived"
      ? colors.textMuted
      : colors.warning;
  return (
    <View style={[styles.pill, { borderColor: accent }]}>
      <Text style={[styles.pillText, { color: accent }]}>{status || "unknown"}</Text>
    </View>
  );
};

const LessonLibraryList = ({
  colors,
  items,
  total,
  offset,
  loading,
  error,
  fetchWithAuth,
  onOpen,
  onPage,
}) => {
  if (loading && !items.length) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="small" color={colors.accentPrimary} />
        <Text style={[styles.mutedLine, { color: colors.textMuted }]}>
          Loading lessons…
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View
        style={[
          styles.notice,
          { backgroundColor: colors.incorrectBg, borderColor: colors.incorrectBorder },
        ]}
      >
        <Text style={[styles.noticeText, { color: colors.danger }]} selectable>
          {error}
        </Text>
      </View>
    );
  }

  if (!items.length) {
    return (
      <View style={styles.centered}>
        <Text style={[styles.mutedLine, { color: colors.textMuted }]}>
          No lessons match these filters.
        </Text>
      </View>
    );
  }

  const hasPrev = offset > 0;
  // A null total means the server did not say how many there are. A full page
  // is then the only evidence that another one exists.
  const hasNext =
    total === null ? items.length >= PAGE_SIZE : offset + items.length < total;

  return (
    <View>
      {items.map((item) => (
        <View key={item.id} style={styles.rowWrap}>
          <TouchableOpacity
            onPress={() => onOpen(item)}
            accessibilityRole="button"
            accessibilityLabel={`Edit ${item.title}`}
            style={[
              styles.row,
              { backgroundColor: colors.panelBg, borderColor: colors.border },
            ]}
          >
            <View style={styles.rowMain}>
              <View style={styles.rowTitleLine}>
                <Text
                  style={[styles.rowTitle, { color: colors.textPrimary }]}
                  numberOfLines={2}
                >
                  {item.title}
                </Text>
                <StatusPill colors={colors} status={item.status} />
                {item.content_locked ? (
                  <View style={[styles.pill, styles.lockPill, { borderColor: colors.info }]}>
                    <MaterialCommunityIcons name="lock" size={11} color={colors.info} />
                    <Text style={[styles.pillText, { color: colors.info }]}>Locked</Text>
                  </View>
                ) : null}
              </View>

              <Text style={[styles.rowMeta, { color: colors.textMuted }]} numberOfLines={2}>
                {describeLesson(item)}
              </Text>

              {item.content_locked ? (
                <Text style={[styles.lockLine, { color: colors.info }]}>
                  Edited here — an ordinary seed skips this lesson. A forced seed still
                  overwrites it.
                </Text>
              ) : null}
            </View>

            <MaterialCommunityIcons
              name="chevron-right"
              size={20}
              color={colors.textMuted}
            />
          </TouchableOpacity>
          <LessonDownloadButton
            fetchWithAuth={fetchWithAuth}
            lessonPlanId={item.id}
            color={colors.textSecondary}
            borderColor={colors.border}
          />
        </View>
      ))}

      <View style={styles.pager}>
        <Text style={[styles.mutedLine, { color: colors.textMuted }]}>
          {formatCountLabel(offset, items.length, total, "lessons")}
        </Text>
        <View style={{ flex: 1 }} />
        <TouchableOpacity
          onPress={() => onPage(-1)}
          disabled={!hasPrev || loading}
          accessibilityRole="button"
          accessibilityLabel="Previous page"
          style={[
            styles.pageBtn,
            { borderColor: colors.borderStrong, opacity: hasPrev && !loading ? 1 : 0.4 },
          ]}
        >
          <Text style={[styles.pageBtnText, { color: colors.textSecondary }]}>
            Previous
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => onPage(1)}
          disabled={!hasNext || loading}
          accessibilityRole="button"
          accessibilityLabel="Next page"
          style={[
            styles.pageBtn,
            { borderColor: colors.borderStrong, opacity: hasNext && !loading ? 1 : 0.4 },
          ]}
        >
          <Text style={[styles.pageBtnText, { color: colors.textSecondary }]}>Next</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  centered: { alignItems: "center", paddingVertical: 22, gap: 8 },
  mutedLine: { fontSize: 12 },

  notice: { borderRadius: 10, borderWidth: 1, padding: 12, marginBottom: 10 },
  noticeText: { fontSize: 12, lineHeight: 18 },

  rowWrap: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 8 },
  row: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderRadius: 11,
    borderWidth: 1,
    paddingVertical: 11,
    paddingHorizontal: 13,
  },
  rowMain: { flex: 1, minWidth: 0, gap: 4 },
  rowTitleLine: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 7 },
  rowTitle: { fontSize: 13, fontWeight: "700", flexShrink: 1 },
  rowMeta: { fontSize: 11, lineHeight: 16 },
  lockLine: { fontSize: 11, lineHeight: 16, fontWeight: "600" },

  pill: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 8, paddingVertical: 2 },
  lockPill: { flexDirection: "row", alignItems: "center", gap: 4 },
  pillText: {
    fontSize: 9,
    fontWeight: "800",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },

  pager: { flexDirection: "row", alignItems: "center", gap: 8, marginTop: 4 },
  pageBtn: { borderRadius: 8, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 7 },
  pageBtnText: { fontSize: 12, fontWeight: "700" },
});

export default LessonLibraryList;
